/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BuilderPermission, SpecTemplateClauses } from '@gh/builder-shared-data';
import {
  FormStateSnapshot,
  getFormStateSnapshotValue,
  getInvalidFields,
  hasFormStateError,
  isEntityInvalid,
  isRouteForCreationMode,
  markFormStateAsTouched,
  MutablePageStateWithSelection,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';
import { first, last, pull } from 'lodash';
import {
  getJstClauseId,
  hasJstElementError,
  hasJstElementFormError,
  hasJstSectionInvalidClause,
  isJstSection,
  JobSpecTemplateLevel,
  JstClause,
  JstDetails,
  JstElement,
  JstMasterInfo,
  JstParagraph,
  JstParagraphInfo,
  JstSection,
  JstSectionInfo,
  JstSelection,
  jstTreeHelper,
  validateJstMasterInfo,
} from '../../resources/job-spec-template.model';

@Injectable()
export class JobSpecTemplateDetailsFormService extends MutablePageStateWithSelection<JstDetails, JstSelection>({}) {
  constructor(private route: ActivatedRoute,
              private userContext: UserContext) {
    super();
  }

  get id(): Maybe<ID> {
    return this.value.specTemplateId;
  }

  get invalid(): boolean {
    // check validity of top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo) || isNotNil(validateJstMasterInfo(getFormStateSnapshotValue(masterInfo)))) {
      return true;
    }

    // check validity of tree content
    return jstTreeHelper.reduceNodes(
      this.value.paragraphs,
      (invalid: boolean, element: JstElement) =>
        invalid || hasJstElementError(element),
      false);
  }

  highlightAll(): void {
    // highlight top form
    const { masterInfo } = this.value;
    if (hasFormStateError(masterInfo) || validateJstMasterInfo(getFormStateSnapshotValue(masterInfo))) {
      this.value.masterInfo = markFormStateAsTouched(masterInfo);
      this.emitChange();
      return;
    }

    // find the first invalid tree node
    const invalidPath = jstTreeHelper.reduceNodes(
      this.value.paragraphs,
      (invalid, element: JstElement, _1, _2, path) => {
        if (invalid) {
          return invalid;
        }

        return hasJstElementError(element) ? path : void 0;
      },
      <Maybe<any[]>>void 0);

    if (invalidPath) {
      const lastNode = last(invalidPath)!;
      this.selectByPath(invalidPath);
      if (hasJstElementFormError(lastNode)) {
        lastNode.info = markFormStateAsTouched(lastNode.info);
      } else if (isJstSection(lastNode) && hasJstSectionInvalidClause(lastNode)) {
        this.focusInvalidClause(first(lastNode.clauses.filter(isEntityInvalid))!);
      }
      this.emitChange();
    }
  }

  build(details: JstDetails): void {
    this.setMode(isRouteForCreationMode(this.route)
      ? FormMode.CreateMode : (this.userContext.hasPermission(BuilderPermission.Setup.JobAdmin.JobSpecTemplates.EDIT)
        ? FormMode.EditMode : FormMode.ViewMode));
    this.setValue(details);

    this.selectFirstParagraph();
  }

  merge(details: JstDetails): void {
    jstTreeHelper.merge(this.value.paragraphs, details.paragraphs, (thisNode, anotherNode, level) => {
      switch (level) {
        case JobSpecTemplateLevel.Paragraph:
          thisNode.isNew = false;
          thisNode.specTemplateParagraphId = anotherNode.specTemplateParagraphId;
          break;
        case JobSpecTemplateLevel.Section:
          thisNode.isNew = false;
          thisNode.specTemplateSectionId = anotherNode.specTemplateSectionId;
          break;
        default:
          break;
      }
    });

    this.value.version = details.version;
    this.emitChange();
  }

  setAsDefault(isDefault: boolean): void {
    this.value.isForRfq = isDefault;
    this.emitChange();
  }

  updateMasterInfo(masterInfo: FormStateSnapshot<JstMasterInfo>): void {
    this.value.masterInfo = masterInfo;
    this.setDirty();
    this.emitChange();
  }

  updateParagraphInfo(paragraph: JstParagraph, paragraphInfo: FormStateSnapshot<JstParagraphInfo>): void {
    paragraph.info = paragraphInfo;
    this.setDirty();
    this.emitChange();
  }

  updateSectionInfo(section: JstSection, sectionInfo: FormStateSnapshot<JstSectionInfo>): void {
    section.info = sectionInfo;
    this.setDirty();
    this.emitChange();
  }

  updateSectionClausesAre(section: JstSection, clausesAre: SpecTemplateClauses): void {
    section.clausesAre = clausesAre;
    this.setDirty();
    this.emitChange();
  }

  updateSectionClauses(section: JstSection, clauses: JstClause[]): void {
    section.clauses = clauses;
    this.setDirty();
    this.emitChange();
  }

  addParagraph(paragraph: JstParagraph): void {
    this.value.paragraphs.unshift(paragraph);
    this.setDirty();
    this.emitChange();
  }

  removeParagraph(paragraph: JstParagraph): void {
    this.ensureExistingNodeSelected([paragraph]);

    pull(this.value.paragraphs, paragraph);
    this.setDirty();
    this.emitChange();
  }

  addSection(paragraph: JstParagraph, section: JstSection): void {
    paragraph.sections.unshift(section);
    this.setDirty();
    this.emitChange();
  }

  removeSection(paragraph: JstParagraph, section: JstSection): void {
    this.ensureExistingNodeSelected([paragraph, section]);

    pull(paragraph.sections, section);
    this.setDirty();
    this.emitChange();
  }

  selectParagraph(paragraph: JstParagraph): void {
    this.setSelection({
      level: JobSpecTemplateLevel.Paragraph,
      paragraph,
      section: void 0,
      clauseFocusedCell: void 0,
    });
  }

  selectSection(paragraph: JstParagraph, section: JstSection): void {
    this.setSelection({
      level: JobSpecTemplateLevel.Section,
      paragraph,
      section,
      clauseFocusedCell: void 0,
    });
  }

  resetSelection(): void {
    this.setSelection({
      level: void 0,
      paragraph: void 0,
      section: void 0,
      clauseFocusedCell: void 0,
    });
  }

  selectByPath(path: any[]): void {
    switch (path.length) {
      case JobSpecTemplateLevel.Paragraph:
        this.selectParagraph(path[0]);
        break;
      case JobSpecTemplateLevel.Section:
        this.selectSection(path[0], path[1]);
        break;
      default:
        this.resetSelection();
        break;
    }
  }

  resetFocusedCell(): void {
    this.focusClauseCell(void 0);
  }

  moveNodes(): void {
    this.setDirty();
    this.emitChange();
  }

  private selectFirstParagraph(): void {
    const firstParagraph = first(this.value.paragraphs);
    if (firstParagraph) {
      this.selectParagraph(firstParagraph);
    }
  }

  private ensureExistingNodeSelected(removedPath: any[]): void {
    const { selection } = this;
    const path = jstTreeHelper.findNextNode(
      this.value.paragraphs,
      removedPath,
      jstTreeHelper.createPath(selection.paragraph, selection.section));

    this.selectByPath(path);
  }

  private focusInvalidClause(clause: JstClause): void {
    const invalidFields = getInvalidFields(clause);
    const firstInvalidField = first(invalidFields);
    if (firstInvalidField) {
      this.focusClauseCell({ id: getJstClauseId(clause), field: firstInvalidField });
    }
  }

  private focusClauseCell(clauseFocusedCell?: FocusedCell): void {
    this.setSelection({
      ...this.selection,
      clauseFocusedCell,
    });
  }
}
